// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/widget-instance.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { widgetInstancesApi } from "../../api/widget-instances";
import type { WidgetInstance } from "../../api/types";
// override-block:imports BEGIN
// CHG-2460 / ITER-905202 — Widget Studio S3.
import { createElement } from "react";
import type { ReactNode } from "react";
import type { PageHeaderAction } from "../../components/composition/PageHeader";
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
import {
  runWidgetInstanceLifecycle,
  openWidgetInstanceEdit,
} from "../../lib/widget-instance-actions";
// CHG-2473 / ITER-905216 — Widget Studio S6: hand-built Preview + Embed panels
// injected into the override-slots below.
import { WidgetPreviewPanel } from "../../components/widget-studio/WidgetPreviewPanel";
import { WidgetEmbedPanel } from "../../components/widget-studio/WidgetEmbedPanel";
// override-block:imports END

// override-block:helpers BEGIN
// Activation status badge. activation_status is a lifecycle STATUS (not a
// pipeline stage) so it surfaces as a badge per the entity-hint, NOT the
// stage_chip slot. Design-system tone tokens only (composition-lint).
const ACTIVATION_TONE: Record<string, { bg: string; fg: string }> = {
  active:    { bg: "var(--color-success-tint)", fg: "var(--color-success)" },
  pending:   { bg: "var(--color-warning-tint)", fg: "var(--color-warning)" },
  suspended: { bg: "var(--color-warning-tint)", fg: "var(--color-warning)" },
  disabled:  { bg: "var(--color-bg-subtle)",    fg: "var(--color-text-secondary)" },
  archived:  { bg: "var(--color-bg-subtle)",    fg: "var(--color-text-secondary)" },
  blocked:   { bg: "var(--color-error-tint)",   fg: "var(--color-error)" },
};

function activationBadge(value: string | null | undefined): ReactNode {
  const status = (value ?? "").toString();
  const tone = ACTIVATION_TONE[status] ?? {
    bg: "var(--color-bg-subtle)",
    fg: "var(--color-text-secondary)",
  };
  return createElement(
    "span",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        height: 22,
        padding: "0 10px",
        fontSize: 12,
        fontWeight: 600,
        background: tone.bg,
        color: tone.fg,
        borderRadius: 9999,
        textTransform: "capitalize",
      },
    },
    status ? status.replace(/_/g, " ") : "—",
  );
}

// when:-gates from the entity-hint primary_actions — only the legal lifecycle
// transition renders. activation_status / registration_status mutate ONLY
// through these named verbs (never PATCH); the BE re-enforces each guard.
function canApprove(r: WidgetInstance): boolean {
  return r.activation_status === "pending" || r.registration_status === "discovered";
}
function canActivate(r: WidgetInstance): boolean {
  return r.activation_status === "pending" || r.activation_status === "disabled";
}
function canDisable(r: WidgetInstance): boolean {
  return r.activation_status === "active";
}
function canBlock(r: WidgetInstance): boolean {
  return r.activation_status !== "blocked";
}

// Endpoint-citing placeholder card for the generator-emitted data tabs.
// Installations / Observations surface their full tables in S4 / S5; Analytics
// (the by-page report) is contract-complete but un-implemented on the BE.
// Mirrors the campaign-detail placeholder pattern — design-system tokens only.
function tabCard(body: string): ReactNode {
  return createElement(
    "div",
    {
      className: "card",
      style: { padding: 16, color: "var(--color-text-muted)", fontSize: 13, lineHeight: 1.6 },
    },
    body,
  );
}
// override-block:helpers END

export const widgetInstanceDetailConfig: DetailPageConfig<WidgetInstance> = {
  idParam: "widgetInstanceId",
  queryKey: (id: string) => ["widget-instances", "detail", id] as const,
  queryFn: (id: string) => widgetInstancesApi.getWidgetInstance(id),
  backTo: "/channels/widgets",
  // override-block:title BEGIN
  title: (r) => r.display_name ?? r.instance_key,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => r.default_domain ?? null,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    {
      id: "impressions",
      label: "Impressions",
      value: r.impressions,
      format: "number",
      tone: "g-events",
    },
    {
      id: "viewable",
      label: "Viewable",
      value: r.viewable_impressions,
      format: "number",
      tone: "g-events",
    },
    {
      id: "clicks",
      label: "Clicks",
      value: r.cta_clicks,
      format: "number",
      tone: "g-mygiving",
    },
    // override-block:statcard-activation BEGIN
    {
      id: "activation",
      label: "Activation",
      value: null,
      format: "text",
      tone: "g-people",
      render: activationBadge(r.activation_status),
    },
    // override-block:statcard-activation END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    // Lifecycle verbs. RBAC-gated (GATE_CATALOG.widget_detail) AND when:-gated
    // on the current activation_status (canApprove/… helpers) so only the legal
    // transition renders. onClick routes to the imperative host
    // (lib/widget-instance-actions); the BE re-enforces both gates.
    {
      id: "edit",
      label: "Edit",
      variant: "secondary",
      permissionGate: toGate(GATE_CATALOG.widget_detail["edit"], "disable"),
      onClick: () => openWidgetInstanceEdit(_r),
    },
    ...(canApprove(_r)
      ? ([
          {
            id: "approve",
            label: "Approve",
            variant: "primary",
            permissionGate: toGate(GATE_CATALOG.widget_detail["approve"], "hide"),
            onClick: () => runWidgetInstanceLifecycle("approve", _r),
          },
        ] as PageHeaderAction[])
      : []),
    ...(canActivate(_r)
      ? ([
          {
            id: "activate",
            label: "Activate",
            variant: "primary",
            permissionGate: toGate(GATE_CATALOG.widget_detail["activate"], "hide"),
            onClick: () => runWidgetInstanceLifecycle("activate", _r),
          },
        ] as PageHeaderAction[])
      : []),
    ...(canDisable(_r)
      ? ([
          {
            id: "disable",
            label: "Disable",
            variant: "ghost",
            permissionGate: toGate(GATE_CATALOG.widget_detail["disable"], "hide"),
            onClick: () => runWidgetInstanceLifecycle("disable", _r),
          },
        ] as PageHeaderAction[])
      : []),
    ...(canBlock(_r)
      ? ([
          {
            id: "block",
            label: "Block",
            variant: "ghost",
            permissionGate: toGate(GATE_CATALOG.widget_detail["block"], "hide"),
            onClick: () => runWidgetInstanceLifecycle("block", _r),
          },
        ] as PageHeaderAction[])
      : []),
    // override-block:actions END
  ],
  tabs: [
    // override-block:tab-overview BEGIN
    {
      id: "overview",
      label: "Overview",
      render: (r, fmt) => {
        const rows: Array<[string, string]> = [
          ["Instance key", r.instance_key ?? "—"],
          ["Scope", (r.instance_scope ?? "—").toString().replace(/_/g, " ")],
          ["Registration source", (r.registration_source ?? "—").toString().replace(/_/g, " ")],
          ["Registration status", (r.registration_status ?? "—").toString().replace(/_/g, " ")],
          ["Default URL pattern", r.default_url_pattern ?? "—"],
          ["First seen", r.first_seen_at ? fmt.formatDate(r.first_seen_at, "long") : "—"],
          ["Last seen", r.last_seen_at ? fmt.formatDate(r.last_seen_at, "relative") : "—"],
          ["Activated", r.activated_at ? fmt.formatDate(r.activated_at, "long") : "—"],
          ["Approved", r.approved_at ? fmt.formatDate(r.approved_at, "long") : "—"],
        ];
        return createElement(
          "div",
          { className: "card", style: { padding: 16, fontSize: 13, lineHeight: 1.8 } },
          ...rows.map(([k, v]) =>
            createElement(
              "div",
              { key: k, style: { display: "flex", justifyContent: "space-between", gap: 16 } },
              createElement("span", { style: { color: "var(--color-text-muted)" } }, k),
              createElement("span", { style: { textTransform: "capitalize" } }, v),
            ),
          ),
        );
      },
    },
    // override-block:tab-overview END
    // override-block:tab-installations BEGIN
    {
      id: "installations",
      label: "Installations",
      render: (r) =>
        tabCard(
          `Observed embed locations of this widget — GET /admin/widgets/installations?widget_instance_id=${r.id}. Full table lands in S4.`,
        ),
    },
    // override-block:tab-installations END
    // override-block:tab-observations BEGIN
    {
      id: "observations",
      label: "Observations",
      render: (r) =>
        tabCard(
          `Per-page telemetry for this widget — GET /admin/widgets/page-observations?widget_instance_id=${r.id}. Full table lands in S5.`,
        ),
    },
    // override-block:tab-observations END
    // override-block:tab-analytics BEGIN
    {
      id: "analytics",
      label: "Analytics",
      render: (r) =>
        tabCard(
          `Usage + conversion by page — GET /admin/widgets/analytics/by-page?instance=${r.id}. Report is contract-complete; the grouped-aggregate UI lands when the BE report ships.`,
        ),
    },
    // override-block:tab-analytics END
    // override-block:tab-preview BEGIN
    {
      id: "preview",
      label: "Preview",
      // slot_component: WidgetPreviewPanel — GET /admin/widgets/instances/{id}/preview
      render: (r) => createElement(WidgetPreviewPanel, { instanceId: r.id }),
    },
    // override-block:tab-preview END
    // override-block:tab-embed BEGIN
    {
      id: "embed",
      label: "Embed",
      // slot_component: WidgetEmbedPanel — POST /admin/widgets/instances/{id}/embed-code
      render: (r) => createElement(WidgetEmbedPanel, { instanceId: r.id }),
    },
    // override-block:tab-embed END
    // override-block:custom-tabs BEGIN
    // override-block:custom-tabs END
  ],
};
